import { useState } from "react";
import { Card, Icon } from "@mui/material";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import BasicTable from "ui-components/tables/BasicTable";
import { ListLayout } from "ui-components/Layout";
import { DelButton, MoreButton } from "ui-components/IconButton";
import { Success, Error, MessageDialog } from "ui-components/Alert";
import {
  getPreferredLocationList,
  deactivatePreferredLocation,
  activatePreferredLocation,
  getPreferredLocationStatusCount,
} from "services/data.internal.service";
import BasicTabs from "ui-components/BasicTabs";
import { useCustomerValue } from "providers/CustomerContext";
import { TableCard } from "ui-components/Layout";
import { ActiveButton } from "ui-components/IconButton";
import { useTeamValue } from "providers/TeamContext";

function PreferredLocationList() {
  const { customerCode } = useCustomerValue();
  const {permissions, allowAll} = useTeamValue();
  const navigate = useNavigate();
  const [alert, setAlert] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [data, setData] = useState([]);
  const [status, setStatus] = useState("Active");
  const active = status === "Active" ? true : false;
  const [tabs, setTabs] = useState([
    { label: "Active", count: 0 },
    { label: "Inactive", count: 0 },
  ]);
  const queryClient = useQueryClient();
  const onError = (data) => setAlert(<Error data={data} onChange={setAlert} />);

  const addPermission = allowAll || (Boolean(permissions["Preferred Location"]) && permissions["Preferred Location"].findIndex(b => b==="new") !== -1);
  const editPermission = allowAll || (Boolean(permissions["Preferred Location"]) && permissions["Preferred Location"].findIndex(b => b==="edit") !== -1);
  const deactivatePermission = allowAll || (Boolean(permissions["Preferred Location"]) && permissions["Preferred Location"].findIndex(b => b==="deactivate") !== -1);
  
  useQuery({
    queryKey: ["preferred-location-list", [active, customerCode]],
    queryFn: () => getPreferredLocationList({ status: active, customerCode: customerCode }),
    onSuccess: (data) => {
      setData(data.data.data);
    },
    onError,
  });

  useQuery({
    queryKey: ["preferred-location-status", [customerCode]],
    queryFn: () => getPreferredLocationStatusCount({ customerCode }),
    onSuccess: (data) => {
      setTabs(data.data.data);
    },
    onError,
  });

  const onSuccess = (data) => {
    queryClient.invalidateQueries(["preferred-location-status"]);
    queryClient.invalidateQueries(["preferred-location-list"]);
    setAlert(<Success data={data} onChange={setAlert} />);
  };

  const delMutation = useMutation(deactivatePreferredLocation, {
    onSuccess,
    onError,
  });
  const activeMutation = useMutation(activatePreferredLocation, {
    onSuccess,
    onError,
  });

  const onDelete = (req) => {
    setDialog(
      <MessageDialog
        title={`Deactivate Preferred Location for Rack ${req.rack} at ${req.warehouseCode} - ${req.zoneID}`}
        onClose={() => setDialog(null)}
        onOk={() => {
          delMutation.mutate(req);
          setDialog(null);
        }}
      />
    );
  };
  const onActive = (req) => {
    setDialog(
      <MessageDialog
        title={`Activate Preferred Location for Rack ${req.rack} at ${req.warehouseCode} - ${req.zoneID}`}
        onClose={() => setDialog(null)}
        onOk={() => {
          activeMutation.mutate(req);
          setDialog(null);
        }}
      />
    );
  };

  const columns = [
    { header: "Warehouse Code", accessorKey: "warehouseCode" },
    { header: "Zone ID", accessorKey: "zoneID" },
    { header: "Rack", accessorKey: "rack" },
    {
      header: "Action",
      accessorKey: "action",
      enableColumnFilter: false,
      cell: ({ row }) => {
        const { warehouseCode, zoneID, rack } = row.original;
        if (active) {
          return (
            <>
              <MoreButton
                options={[
                  {
                    label: "View and Delete Location/SKU",
                    disabled: !editPermission,
                    onClick: () => {
                      navigate("/setup/preferred-location/edit", {
                        state: {
                          warehouseCode: warehouseCode,
                          zoneID: zoneID,
                          rack: rack,
                          mode: "delete",
                        },
                      });
                    },
                  },
                  {
                    label: "Edit Route Sequence",
                    disabled: !editPermission,
                    onClick: () => {
                      navigate("/setup/preferred-location/edit", {
                        state: {
                          warehouseCode: warehouseCode,
                          zoneID: zoneID,
                          rack: rack,
                          mode: "edit",
                        },
                      });
                    },
                  },
                ]}
              />
              <DelButton
                title="deactivate preferred location"
                disabled={!deactivatePermission}
                onClick={() => {
                  onDelete({
                    warehouseCode: warehouseCode,
                    zoneID: zoneID,
                    rack: rack,
                  });
                }}
              />
            </>
          );
        } else {
          return (
            <ActiveButton
              title="activate preferred location"
              disabled={!editPermission}
              onClick={() =>
                onActive({
                  warehouseCode: warehouseCode,
                  zoneID: zoneID,
                  rack: rack,
                })
              }
            />
          );
        }
      },
    },
  ];
  const rows = data.map((d) => {
    const { warehouseCode, zoneID, rack } = d;
    return {
      warehouseCode,
      zoneID,
      rack,
    };
  });
  return (
    <ListLayout>
      {dialog}
      <MDBox my={3}>
        {alert}
        <BasicTabs tabs={tabs} status={status} setStatus={setStatus} />
      </MDBox>
      <TableCard>
        <MDBox display="flex" justifyContent="flex-start" alignItems="flex-start" margin="10px">
          <MDButton
            variant="gradient"
            color="info"
            disabled={!addPermission}
            onClick={() => navigate("/setup/preferred-location/add")}
          >
            <Icon>add</Icon>&nbsp;preferred&nbsp;location
          </MDButton>
          <MDButton
            sx={{ marginLeft: "1rem" }}
            variant="outlined"
            color="dark"
            disabled={!addPermission}
            onClick={() => navigate("/setup/preferred-location/upload")}
          >
            <Icon>upload</Icon> &nbsp;upload
          </MDButton>
        </MDBox>
        <BasicTable columns={columns} data={rows} />
      </TableCard>
    </ListLayout>
  );
}

export default PreferredLocationList;
